import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import client from '../../api/client';
import config from '../../config/config';
import globals from '../../services/globals';
import './SupportWidget.css';

/**
 * Mbështetja, brenda vetë Nexumës.
 *
 * **Pse këtu e jo një email.** Kur dikush nuk hyn dot, ai nuk ka nga t'ia
 * nisë: nuk e di adresën tonë, nuk e di çfarë të shkruajë, dhe zakonisht hesht.
 * Kjo kuti e heq atë hap — ai shkruan aty ku ndodhet, dhe operatori e sheh
 * bashkë me gjendjen e llogarisë së tij.
 *
 * **Asgjë nuk ruhet te Nexuma.** Serveri i saj është transport i firmosur; fija
 * jeton te ControlHub-i, ku rrjedh edhe ajo e pesë produkteve të tjera. Kështu
 * historia i mbijeton çdo rindërtimi, dhe kutia mbetet një.
 *
 * Dy gjendje, jo një: i kyçuri sheh bisedën e vet të plotë; vizitori sheh një
 * formë me email. E dyta ekziston pikërisht për rastin numër një — «s'po hyj
 * dot» — që s'mund të dërgohet nga brenda.
 */

const API = (config.baseUrl || '').replace(/\/+$/, '');

/** E njëjta listë si te ControlHub-i: një kufi më i gjerë do të refuzohej atje. */
const ACCEPT =
  'image/jpeg,image/png,image/gif,image/webp,image/heic,video/mp4,video/quicktime,' +
  'video/webm,application/pdf,application/zip,.doc,.docx,.xls,.xlsx,.ppt,.pptx,.odt,.txt,.csv,.log';

const MAX_BYTES = 25 * 1024 * 1024;

const EMAIL = /^[^@\s]+@[^@\s]+\.[^@\s]+$/;

/** Pyetjet që bëhen më shpesh — një klikim në vend të një fjalie. */
const QUICK = ['signIn', 'password', 'missingData', 'access'];

/**
 * Ora sipas gjuhës së faqes, jo sipas 'sq'.
 *
 * Faqja publike ofron Shqip / English / Deutsch. Një datë e shkruar gjithnjë
 * në shqip te një faqe gjermanisht është e njëjta gabim si teksti i papërkthyer
 * — thjesht më e vështirë për t'u vënë re.
 */
const fmt = (iso, lang) => {
  if (!iso) return '';
  const d = new Date(iso);
  return d.toLocaleString(lang || 'sq', { day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit' });
};

/** Një minutë. Mjaft për të treguar një gabim; jo aq sa të kalojë 25 MB. */
const MAX_RECORDING_MS = 60_000;

/**
 * ~1,5 Mbps: teksti i një gabimi mbetet i lexueshëm, dhe një minutë e plotë rri
 * nën kufirin 25 MB. Pa e caktuar, shfletuesi zgjedh vetë — dhe te ekranet 4K
 * kjo prodhon skedarë që refuzohen pikërisht pasi janë regjistruar.
 */
const RECORDING_BITS = 1_500_000;

/** Formati më i mirë që mbështet ky shfletues; `undefined` ia lë zgjedhjen atij. */
const pickRecordingType = () =>
  ['video/webm;codecs=vp9', 'video/webm;codecs=vp8', 'video/webm', 'video/mp4'].find(
    (t) => typeof MediaRecorder !== 'undefined' && MediaRecorder.isTypeSupported(t),
  );

/**
 * Merr një kuadër të vetëm nga rrjedha e ekranit dhe e kthen si PNG.
 *
 * `<video>` + `<canvas>` e jo `ImageCapture`: ky i dyti është më i shkurtër po
 * mungon te Safari dhe Firefox — pikërisht atje ku dikush mund të jetë kur i del
 * gabimi. Pritet një kuadër i vizatuar para se të lexohet, se përndryshe canvas-i
 * del i zi te disa shfletues.
 *
 * PNG e jo JPEG: teksti i një gabimi duhet të mbetet i lexueshëm.
 */
const grabScreenFrame = (stream) =>
  new Promise((resolve, reject) => {
    const video = document.createElement('video');
    video.srcObject = stream;
    video.muted = true;
    video.playsInline = true;

    // Pa mesazh: thirrësi e zë me tekstin e përkthyer. Një varg i ngurtë këtu
    // do të dilte shqip te faqja gjermanisht, sepse `err.message` ka përparësi.
    const fail = () => reject(new Error(''));
    video.onerror = fail;
    video.onloadedmetadata = () => {
      video
        .play()
        .then(() => new Promise((tick) => requestAnimationFrame(() => tick(null))))
        .then(() => {
          const canvas = document.createElement('canvas');
          canvas.width = video.videoWidth;
          canvas.height = video.videoHeight;
          const ctx = canvas.getContext('2d');
          if (!ctx || canvas.width === 0 || canvas.height === 0) return fail();
          ctx.drawImage(video, 0, 0);
          video.pause();
          video.srcObject = null;
          return canvas.toBlob((blob) => {
            if (!blob) return fail();
            return resolve(new File([blob], 'ekrani.png', { type: 'image/png' }));
          }, 'image/png');
        })
        .catch(fail);
    };
  });

/**
 * Një skedar i fillit.
 *
 * Bajtet merren me `client` — pra me sesionin — dhe kthehen në një adresë
 * `blob:` lokale. Një `<img src>` nuk vendos dot header `Authorization`, dhe
 * alternativa do të ishte tokeni te adresa: ai përfundon te historiku i
 * shfletuesit dhe te log-et e serverit, pra do të ishte çelës i shpërndarë kudo
 * për një pamje të vetme.
 */
function Attachment({ file }) {
  const [url, setUrl] = useState(null);
  const [failed, setFailed] = useState(false);
  const isImage = /^image\//.test(file.mimeType || '');

  useEffect(() => {
    let dead = false;
    let objectUrl = '';
    client
      .get(`${API}/api/support/attachment/${file.id}`, { responseType: 'blob' })
      .then((res) => {
        if (dead) return;
        objectUrl = URL.createObjectURL(res.data);
        setUrl(objectUrl);
      })
      .catch(() => setFailed(true));
    return () => {
      dead = true;
      // Pa këtë, çdo hapje e kutisë lë pas një kopje të skedarit në kujtesë.
      if (objectUrl) URL.revokeObjectURL(objectUrl);
    };
  }, [file.id]);

  if (failed) return <span className="nx-file">{file.filename}</span>;
  if (!url) return <span className="nx-file">Duke ngarkuar…</span>;

  return isImage ? (
    <a href={url} download={file.filename}>
      <img src={url} alt={file.filename} className="nx-shot" />
    </a>
  ) : (
    <a className="nx-file" href={url} download={file.filename}>
      {file.filename}
    </a>
  );
}

/**
 * `showLauncher={false}` hides the floating button; the panel is then opened by
 * the "Helper" entry at the bottom of the sidebar, which dispatches
 * `nexuma:open-support`. App.js passes false only INSIDE the app — on the login
 * page there is no sidebar, and the person who cannot sign in is exactly the
 * one who needs to write.
 *
 * The launcher is still rendered while the panel is open or a recording is
 * running, so there is always a way to close or return to it.
 */
export default function SupportWidget({ showLauncher = true } = {}) {
  const { t, i18n } = useTranslation();
  const [open, setOpen] = useState(false);

  // Anchors the panel beside whatever opened it: the sidebar entry on the left,
  // the floating launcher bottom-right.
  const [anchorLeft, setAnchorLeft] = useState(false);
  const [minimized, setMinimized] = useState(false); // collapsed but still "open" → recording survives

  // Opened from the sidebar entry, since the launcher is hidden inside the app.
  useEffect(() => {
    const onOpen = () => { setAnchorLeft(true); setOpen(true); setMinimized(false); };
    window.addEventListener('nexuma:open-support', onOpen);
    return () => window.removeEventListener('nexuma:open-support', onOpen);
  }, []);
  const [signedIn, setSignedIn] = useState(false);
  const [messages, setMessages] = useState([]);
  const [loaded, setLoaded] = useState(false);
  const [draft, setDraft] = useState('');
  const [email, setEmail] = useState('');
  const [honeypot, setHoneypot] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const [sentPublic, setSentPublic] = useState(false);

  const endRef = useRef(null);
  const fileRef = useRef(null);

  /**
   * Kapja dhe regjistrimi i ekranit.
   *
   * Të dyja varen nga `getDisplayMedia`, që kërkon kontekst të sigurt (HTTPS ose
   * localhost) dhe mungon te shfletuesit e telefonit — ndaj butonat fshihen kur
   * s'mbështeten: një buton që s'bën gjë është më keq se mungesa e tij.
   *
   * Regjistrimi NUK është ndarje e drejtpërdrejtë e ekranit: operatori nuk sheh
   * live. Ajo do të kërkonte WebRTC me server sinjalizimi. Për një gabim që
   * riprodhohet, një video e dërguar bën të njëjtën punë dhe s'varet nga ora e
   * askujt.
   */
  const [canCapture, setCanCapture] = useState(false);
  const [capturing, setCapturing] = useState(false);
  const [recording, setRecording] = useState(false);
  const [recordedMs, setRecordedMs] = useState(0);
  const [dragging, setDragging] = useState(false);
  const recorderRef = useRef(null);

  /**
   * Kush je — pyetur te i njëjti vend ku e di pjesa tjetër e app-it.
   *
   * Kontrollohet sa herë hapet kutia e jo një herë te montimi: dikush mund të
   * hyjë ose të dalë ndërkohë, dhe një gjendje e ngrirë do t'i tregonte formën
   * publike një përdoruesi të kyçur.
   */
  useEffect(() => {
    if (!open) return;
    try {
      const user = globals().getLogedInUser && globals().getLogedInUser();
      setSignedIn(Boolean(user && user.token));
    } catch (e) {
      setSignedIn(false);
    }
  }, [open]);

  const load = useCallback(async () => {
    try {
      const res = await client.get(`${API}/api/support/thread`);
      setMessages((res.data && res.data.messages) || []);
    } catch (e) {
      // Një fill që s'lexohet nuk duhet ta bllokojë shkrimin: ai mbetet bosh
      // dhe klienti mund të provojë prapë.
      setMessages([]);
    } finally {
      setLoaded(true);
    }
  }, []);

  useEffect(() => {
    if (!open || !signedIn) return undefined;
    load();
    // Fija rifreskohet sa është e hapur — përndryshe përgjigjja e operatorit do
    // të mbërrinte vetëm pasi klienti ta mbyllte e ta rihapte kutinë.
    const t = setInterval(load, 15000);
    return () => clearInterval(t);
  }, [open, signedIn, load]);

  useEffect(() => {
    if (endRef.current) endRef.current.scrollIntoView({ block: 'nearest' });
  }, [messages]);

  async function send(text) {
    const body = (text || draft).trim();
    if (!body || busy) return;
    setBusy(true);
    setError('');
    try {
      await client.post(`${API}/api/support/message`, { body });
      setDraft('');
      await load();
    } catch (e) {
      const data = e && e.response && e.response.data;
      setError((data && data.error) || t('support.errSend'));
    } finally {
      setBusy(false);
    }
  }

  async function upload(file) {
    if (!file || busy) return;
    if (file.size > MAX_BYTES) {
      setError(t('support.errTooBig'));
      return;
    }
    setBusy(true);
    setError('');
    try {
      const q = new URLSearchParams({ filename: file.name });
      if (draft.trim()) q.set('caption', draft.trim());
      /**
       * Bajtet shkojnë të papërpunuara.
       *
       * `Content-Type: application/octet-stream` e ndal axios-in t'i kthejë në
       * JSON — dhe firma te serveri mbulon `sha256` e tyre, ndaj çdo ri-kodim
       * do ta prishte heshtazi.
       */
      await client.post(`${API}/api/support/attachment?${q.toString()}`, file, {
        headers: { 'Content-Type': 'application/octet-stream' },
      });
      setDraft('');
      await load();
    } catch (e) {
      const data = e && e.response && e.response.data;
      setError((data && data.error) || t('support.errUpload'));
    } finally {
      setBusy(false);
      if (fileRef.current) fileRef.current.value = '';
    }
  }

  useEffect(() => {
    setCanCapture(
      typeof navigator !== 'undefined' &&
        Boolean(navigator.mediaDevices && navigator.mediaDevices.getDisplayMedia),
    );
  }, []);

  const stopRecording = useCallback(() => {
    // `stop()` shkakton `onstop`, ku mblidhet skedari dhe mbyllen gjurmët.
    if (recorderRef.current && recorderRef.current.state === 'recording') {
      recorderRef.current.stop();
    }
  }, []);

  // Regjistrimi s'duhet t'i mbijetojë mbylljes së panelit apo daljes nga llogaria.
  useEffect(() => {
    if (!open || !signedIn) stopRecording();
  }, [open, signedIn, stopRecording]);

  useEffect(() => {
    if (!recording) return undefined;
    const started = performance.now();
    const tick = setInterval(() => setRecordedMs(performance.now() - started), 250);
    const limit = setTimeout(stopRecording, MAX_RECORDING_MS);
    return () => {
      clearInterval(tick);
      clearTimeout(limit);
    };
  }, [recording, stopRecording]);

  async function captureScreen() {
    if (capturing || recording || busy) return;
    setCapturing(true);
    setError('');
    let stream = null;
    try {
      stream = await navigator.mediaDevices.getDisplayMedia({ video: true, audio: false });
      const shot = await grabScreenFrame(stream);
      await upload(shot);
    } catch (err) {
      // Anulimi te dialogu i shfletuesit nuk është gabim — s'ka pse të bërtasë.
      const name = err && err.name;
      if (name !== 'NotAllowedError' && name !== 'AbortError') {
        setError((err && err.message) || t('support.errScreenshot'));
      }
    } finally {
      // Rrjedha mbyllet gjithnjë: pa këtë, shiriti «po ndahet ekrani» rri aty.
      if (stream) stream.getTracks().forEach((t) => t.stop());
      setCapturing(false);
    }
  }

  async function startRecording() {
    if (recording || capturing || busy) return;
    setError('');
    let stream;
    try {
      // `audio: true` i shfaq shfletuesit kutinë «ndaj edhe zërin» — zgjedhja
      // mbetet e përdoruesit. Mikrofoni nuk kërkohet kurrë.
      stream = await navigator.mediaDevices.getDisplayMedia({ video: true, audio: true });
    } catch (err) {
      const name = err && err.name;
      if (name !== 'NotAllowedError' && name !== 'AbortError') {
        setError(t('support.errRecordStart'));
      }
      return;
    }

    let recorder;
    try {
      const mimeType = pickRecordingType();
      recorder = new MediaRecorder(stream, {
        ...(mimeType ? { mimeType } : {}),
        videoBitsPerSecond: RECORDING_BITS,
      });
    } catch (e) {
      stream.getTracks().forEach((t) => t.stop());
      setError(t('support.errRecordUnsupported'));
      return;
    }

    const chunks = [];
    recorder.ondataavailable = (e) => {
      if (e.data && e.data.size > 0) chunks.push(e.data);
    };
    recorder.onstop = () => {
      stream.getTracks().forEach((t) => t.stop());
      recorderRef.current = null;
      setRecording(false);
      setRecordedMs(0);
      if (chunks.length === 0) {
        setError(t('support.errRecordEmpty'));
        return;
      }
      const type = (recorder.mimeType || 'video/webm').split(';')[0];
      void upload(new File(chunks, `ekrani.${type === 'video/mp4' ? 'mp4' : 'webm'}`, { type }));
    };

    // Ndalimi nga shiriti i shfletuesit është rruga që përdoruesi gjen i pari;
    // pa këtë, regjistrimi do të mbetej «në vazhdim» me një burim të vdekur.
    const track = stream.getVideoTracks()[0];
    if (track) track.addEventListener('ended', stopRecording);

    recorderRef.current = recorder;
    recorder.start();
    setRecording(true);
  }

  async function sendPublic() {
    const body = draft.trim();
    if (!body || !EMAIL.test(email.trim()) || busy) return;
    setBusy(true);
    setError('');
    try {
      const res = await fetch(`${API}/api/support/public`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email: email.trim(), body, website: honeypot }),
      });
      const data = await res.json().catch(() => ({}));
      /**
       * Vetëm një përgjigje e vërtetë e kalon formën te «E morëm».
       *
       * Kur kufiri kalohet, serveri kthen 429 me fjalët e veta — dhe ato duhen
       * lexuar këtu. Një «u dërgua» i rremë do ta linte njeriun të ikte i qetë
       * duke pritur një përgjigje që s'do të vijë kurrë.
       */
      if (!res.ok) {
        setError(data.error || t('support.errGeneric'));
        return;
      }
      setDraft('');
      setSentPublic(true);
    } catch (e) {
      setError(t('support.errGeneric'));
    } finally {
      setBusy(false);
    }
  }

  const panelShown = open && !minimized;
  const recSecs = Math.floor(recordedMs / 1000);
  const recLabel = `${String(Math.floor(recSecs / 60)).padStart(2, '0')}:${String(recSecs % 60).padStart(2, '0')}`;

  return (
    <>
      {/* When the launcher is hidden (inside the app) it still appears while the
          panel is open or a recording is running — otherwise a minimized
          recording would have no way back. Chat owns the bottom-right slot
          then, so it stacks above it instead of covering it. */}
      {(showLauncher || panelShown || recording) && (
      <button
        type="button"
        className={
          `nx-support-fab${panelShown ? ' is-open' : ''}${recording ? ' is-rec' : ''}`
          + `${showLauncher ? '' : ' is-stacked'}`
          + `${showLauncher && !panelShown && !recording ? ' is-labelled' : ''}`
        }
        onClick={() => {
          // Restore from minimized, otherwise toggle. Fully closing also drops the
          // minimized state (and, per the effect above, stops any recording).
          if (open && minimized) { setMinimized(false); return; }
          setAnchorLeft(false);   // opened from the launcher → sit beside it
          setOpen((v) => { const next = !v; if (!next) setMinimized(false); return next; });
        }}
        aria-label={panelShown
          ? t('support.launcherClose')
          : (recording ? t('support.launcherRecording') : t('support.launcher'))}
        title={recording && !panelShown ? `${t('support.launcher')} · ${recLabel}` : undefined}
      >
        {recording && !panelShown
          ? <span className="nx-fab-rec"><span className="nx-fab-recdot" />{recLabel}</span>
          : (panelShown ? '✕' : (
            <>
              <svg className="nx-fab-chat" width="27" height="27" viewBox="0 0 24 24" aria-hidden="true">
                <path fill="#fff" d="M12 3C6.5 3 2 6.58 2 11c0 2.05.98 3.92 2.6 5.32-.1 1.2-.55 2.32-1.32 3.2-.23.27-.04.7.31.66 1.64-.16 3.2-.78 4.45-1.78 1.2.38 2.5.6 3.96.6 5.5 0 10-3.58 10-8S17.5 3 12 3Z"/>
                <circle cx="8" cy="11" r="1.3" fill="#3E8FA3"/>
                <circle cx="12" cy="11" r="1.3" fill="#3E8FA3"/>
                <circle cx="16" cy="11" r="1.3" fill="#3E8FA3"/>
              </svg>
              {/* Etiketa del vetëm te faqja publike. Brenda app-it butoni njihet
                  nga vendi ku rri; një vizitori të parë një rreth pa fjalë s'i
                  thotë asgjë — dhe ai s'e klikon diçka që s'e di ç'është. */}
              {showLauncher && <span className="nx-fab-label">{t('support.launcher')}</span>}
            </>
          ))}
      </button>
      )}

      {panelShown && (
        <section className={`nx-support${anchorLeft ? ' is-left' : ''}`} role="dialog" aria-label={t('support.dialog')}>
          <header className="nx-support-head">
            <div className="nx-support-head-main">
              <strong>{t('support.title')}</strong>
              <span>{t(signedIn ? 'support.subtitleSignedIn' : 'support.subtitleGuest')}</span>
            </div>
            <div className="nx-support-head-actions">
              <button
                type="button" className="nx-head-btn" onClick={() => setMinimized(true)}
                title={t(recording ? 'support.minimizeRecording' : 'support.minimize')}
                aria-label={t('support.minimize')}
              >–</button>
              <button
                type="button" className="nx-head-btn" onClick={() => { setMinimized(false); setOpen(false); }}
                title={t('support.close')} aria-label={t('support.close')}
              >✕</button>
            </div>
          </header>

          <div className="nx-support-body">
            {/*
              Pyetjet e shpeshta rrinë vetëm derisa të nisë biseda: pasi ka
              mesazhe, ato do të ishin zhurmë mbi atë që u tha.
            */}
            {(signedIn ? loaded && messages.length === 0 : !sentPublic) && (
              <div className="nx-support-quick">
                <p>{t('support.quickTitle')}</p>
                {QUICK.map((key) => {
                  const q = t(`support.quick.${key}`);
                  return (
                    <button
                      key={key}
                      type="button"
                      onClick={() => (signedIn ? send(q) : setDraft(q))}
                    >
                      {q}
                    </button>
                  );
                })}
              </div>
            )}

            {signedIn && !loaded && <p className="nx-support-note">{t('support.loading')}</p>}

            {signedIn &&
              messages.map((m) => (
                <div
                  key={m.id}
                  className={`nx-msg ${m.from === 'you' ? 'is-you' : 'is-support'}`}
                >
                  {m.from === 'bot' && <b className="nx-msg-who">{t('support.bot')}</b>}
                  <p>{m.body}</p>
                  {(m.attachments || []).map((a) => (
                    <Attachment key={a.id} file={a} />
                  ))}
                  <time>{fmt(m.at, i18n.language)}</time>
                </div>
              ))}
            <div ref={endRef} />
          </div>

          {/*
            Gjatë regjistrimit thuhet sa kohë ka kaluar.
            Kush harron që po regjistron dërgon një minutë të tërë pa kuptim —
            ose diçka private.
          */}
          {recording && (
            <button type="button" className="nx-recording" onClick={stopRecording}>
              {t('support.recordingBanner', { seconds: Math.floor(recordedMs / 1000) })}
            </button>
          )}

          {error && <p className="nx-support-error">{error}</p>}

          {signedIn && (
            <div
              className={`nx-support-compose${dragging ? ' is-drop' : ''}`}
              /*
                Tërheq-e-lësho: e njëjta rrugë si butoni, pa asnjë hap më shumë.
                Kush ka bërë një pamje ekrani e ka skedarin nën dorë — dhe
                tërheqja është veprimi që provon i pari.
              */
              onDragOver={(e) => {
                e.preventDefault();
                setDragging(true);
              }}
              onDragLeave={() => setDragging(false)}
              onDrop={(e) => {
                const file = e.dataTransfer && e.dataTransfer.files && e.dataTransfer.files[0];
                setDragging(false);
                if (file) {
                  e.preventDefault();
                  upload(file);
                }
              }}
            >
              <input
                ref={fileRef}
                type="file"
                accept={ACCEPT}
                hidden
                onChange={(e) => upload(e.target.files && e.target.files[0])}
              />
              <button
                type="button"
                className="nx-attach"
                onClick={() => fileRef.current && fileRef.current.click()}
                disabled={busy}
                title={t('support.attach')}
                aria-label={t('support.attachAria')}
              >
                📎
              </button>

              {/*
                Butonat e ekranit dalin vetëm kur shfletuesi i mbështet — një
                buton që s'bën gjë është më keq se mungesa e tij.
              */}
              {canCapture && (
                <>
                  <button
                    type="button"
                    className="nx-attach"
                    onClick={captureScreen}
                    disabled={busy || capturing || recording}
                    title={t('support.screenshot')}
                    aria-label={t('support.screenshotAria')}
                  >
                    🖥
                  </button>
                  <button
                    type="button"
                    className={`nx-attach${recording ? ' is-rec' : ''}`}
                    onClick={recording ? stopRecording : startRecording}
                    disabled={busy || capturing}
                    title={t(recording ? 'support.recordStop' : 'support.record')}
                    aria-label={t(recording ? 'support.recordStop' : 'support.recordAria')}
                  >
                    {recording ? '■' : '⏺'}
                  </button>
                </>
              )}
              <textarea
                value={draft}
                onChange={(e) => setDraft(e.target.value)}
                placeholder={t('support.placeholder')}
                rows={2}
                maxLength={4000}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' && !e.shiftKey) {
                    e.preventDefault();
                    send();
                  }
                }}
              />
              <button type="button" className="nx-send" onClick={() => send()} disabled={busy || !draft.trim()}>
                {busy ? '…' : t('support.send')}
              </button>
            </div>
          )}

          {!signedIn && !sentPublic && (
            <div className="nx-support-public">
              <p className="nx-support-note">
                {t('support.guestNote')}
              </p>
              <input
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder={t('support.emailPlaceholder')}
                autoComplete="email"
              />
              {/*
                Fusha e fshehur: një njeri s’e sheh dot, ndaj s’e mbush kurrë.
                Robotët i mbushin të gjitha — dhe kjo mjafton për t’i ndarë, pa i
                kërkuar klientit të zgjidhë enigma pikërisht kur është i mbyllur
                jashtë.
              */}
              <input
                type="text"
                className="nx-hp"
                tabIndex={-1}
                autoComplete="off"
                aria-hidden="true"
                value={honeypot}
                onChange={(e) => setHoneypot(e.target.value)}
              />
              <textarea
                value={draft}
                onChange={(e) => setDraft(e.target.value)}
                placeholder={t('support.placeholder')}
                rows={3}
                maxLength={4000}
              />
              <button
                type="button"
                className="nx-send"
                onClick={sendPublic}
                disabled={busy || !draft.trim() || !EMAIL.test(email.trim())}
              >
                {busy ? t('support.sending') : t('support.send')}
              </button>
            </div>
          )}

          {!signedIn && sentPublic && (
            <div className="nx-support-public">
              <p className="nx-support-done">{t('support.received')}</p>
              <p className="nx-support-note">
                {t('support.replyTo')} <b>{email}</b>.
              </p>
            </div>
          )}
        </section>
      )}
    </>
  );
}
